// Top of every employee card on phones / tablets (Employees, Attendance,
// Payroll): the name is the card title on its own line, and a quiet strip
// underneath carries the contact line (email, or a fallback such as the date)
// on one side and the status badge on the other. The badge used to share the
// title row and squeezed long names into "Mohamed Ab…".
import React from "react";
import { Mail } from "lucide-react";

export default function EmployeeCardHeader({
  theme,
  name,
  subtitle, // second line under the name (code, department, date…)
  email, // shown in the strip with a mail icon
  info, // fallback strip text when there is no email
  infoIcon: InfoIcon, // icon for `info`
  status, // status badge node
  leading, // avatar / selection dot before the name
  onNameClick,
}) {
  const dark = theme === "dark";
  const muted = dark ? "rgba(226,232,240,0.62)" : "#64748b";
  const strong = dark ? "#f1f5f9" : "#0f172a";
  const stripText = email || info;
  const StripIcon = email ? Mail : InfoIcon;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
        {leading}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            onClick={onNameClick}
            role={onNameClick ? "button" : undefined}
            tabIndex={onNameClick ? 0 : undefined}
            onKeyDown={
              onNameClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onNameClick();
                    }
                  }
                : undefined
            }
            title={typeof name === "string" ? name : undefined}
            style={{
              fontSize: "15px",
              fontWeight: 700,
              lineHeight: 1.35,
              color: strong,
              cursor: onNameClick ? "pointer" : "default",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "anywhere",
            }}
          >
            {name || "—"}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: "12px",
                color: muted,
                marginTop: "2px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {(stripText || status) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            padding: "8px 10px",
            borderRadius: "10px",
            background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc",
            border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.05)",
            minWidth: 0,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              minWidth: 0,
              fontSize: "12.5px",
              color: muted,
            }}
            title={stripText || undefined}
          >
            {stripText && StripIcon && <StripIcon size={14} style={{ flexShrink: 0 }} />}
            <span
              dir={email ? "ltr" : undefined}
              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
            >
              {stripText || ""}
            </span>
          </span>
          {status && <span style={{ flexShrink: 0, display: "inline-flex" }}>{status}</span>}
        </div>
      )}
    </div>
  );
}
